import modelCatalog from '../../ai/aiModels.json';
import AI_CONFIG from '../../ai/aiConfig';

const ROUTING = AI_CONFIG?.routing || {};
const ORCHESTRATION = AI_CONFIG?.chatOrchestration || {};

const normalizeModel = (model = {}, defaultTier = 'budget') => {
  const key = String(model?.key || '').trim();
  const id = String(model?.id || '').trim();
  if (!key || !id) return null;

  return {
    key,
    id,
    label: String(model?.label || id).trim(),
    description: String(model?.description || '').trim(),
    tier: String(model?.tier || defaultTier).trim().toLowerCase(),
    pricing: {
      promptPer1M: Number(model?.pricing?.promptPer1M || 0),
      completionPer1M: Number(model?.pricing?.completionPer1M || 0),
      currency: String(model?.pricing?.currency || 'USD').trim() || 'USD'
    },
    selectable: model?.selectable !== false
  };
};

const flattenModels = () => {
  const sections = ['free', 'budget', 'mid-tier', 'premium'];
  return sections.flatMap((section) => {
    const rows = Array.isArray(modelCatalog?.[section]) ? modelCatalog[section] : [];
    return rows.map((row) => normalizeModel(row, section)).filter(Boolean);
  });
};

const MODELS = flattenModels();
const MODEL_BY_ID = new Map(MODELS.map((model) => [String(model.id), model]));
const MODEL_BY_KEY = new Map(MODELS.map((model) => [String(model.key), model]));

const normalizeModelIdVariant = (value = '') => {
  let normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';

  // Normalize known dotted Claude version ids that providers may emit with dash separators.
  normalized = normalized.replace(/claude-(\d+)-(\d+)-/g, 'claude-$1.$2-');
  // Normalize common OpenRouter Anthropic Sonnet 4 release ids to the configured catalog id.
  normalized = normalized.replace(/anthropic\/claude-4-sonnet(?:-\d{6,8})?/g, 'anthropic/claude-sonnet-4');
  // Normalize common DeepSeek V3 naming variants to catalog id.
  normalized = normalized.replace(/deepseek-chat-v\d+[\w-]*/g, 'deepseek-chat');
  normalized = normalized.replace(/deepseek\/deepseek-chat-v\d+[\w-]*/g, 'deepseek/deepseek-chat');
  normalized = normalized.replace(/^deepseek-chat$/, 'deepseek/deepseek-chat');
  // Normalize optional OpenRouter suffix variants.
  normalized = normalized.replace(/:free$/, '');
  return normalized;
};

const getModelIdCandidates = (rawModelId = '') => {
  const raw = String(rawModelId || '').trim().toLowerCase();
  if (!raw) return [];

  const candidates = new Set([raw, normalizeModelIdVariant(raw)]);

  // If the id has extra routing segments, keep trailing provider/model segments too.
  const segments = raw.split('/').filter(Boolean);
  if (segments.length >= 2) {
    const tail2 = `${segments[segments.length - 2]}/${segments[segments.length - 1]}`;
    candidates.add(tail2);
    candidates.add(normalizeModelIdVariant(tail2));
  }

  // Common DeepSeek provider variants seen in telemetry.
  if (raw.includes('deepseek-chat-v3') || raw.includes('deepseek/deepseek-chat-v3')) {
    candidates.add('deepseek/deepseek-chat');
  }
  if (raw === 'deepseek-chat-v3' || raw.startsWith('deepseek-chat-v3-')) {
    candidates.add('deepseek/deepseek-chat');
  }

  return Array.from(candidates).filter(Boolean);
};

const MODEL_BY_NORMALIZED_ID = new Map(
  MODELS.map((model) => [normalizeModelIdVariant(model.id), model])
);

const resolveModelIdByKey = (key) => {
  const model = MODEL_BY_KEY.get(String(key || '').trim());
  return model?.id || null;
};

const resolveFirstModelByTierOrder = (tiers = []) => {
  const normalized = Array.isArray(tiers)
    ? tiers.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
    : [];

  for (const tier of normalized) {
    const row = MODELS.find((model) => model.tier === tier);
    if (row?.id) return row.id;
  }

  return MODELS[0]?.id || null;
};

const DEFAULT_CHEAP_MODEL_ID = resolveModelIdByKey(ROUTING?.defaultCheapModelKey)
  || resolveFirstModelByTierOrder(ROUTING?.fallbackTierOrder || ['budget', 'free', 'mid-tier', 'premium']);

const DEFAULT_RESPONSE_MODEL_ID = resolveModelIdByKey(ROUTING?.defaultResponseModelKey)
  || DEFAULT_CHEAP_MODEL_ID;

const normalizeTaskKey = (taskKey = '') => {
  const raw = String(taskKey || '').trim().toLowerCase();
  if (raw === 'chat.ask') return 'chat.explore';
  if (raw === 'chat.edit') return 'chat.write';
  return raw;
};

const TASK_RESPONSE_MODEL_KEYS = ROUTING?.taskResponseModelKeys && typeof ROUTING.taskResponseModelKeys === 'object'
  ? ROUTING.taskResponseModelKeys
  : {};

const TASK_LABELS = {
  'chat.explore': 'Chat Explore',
  'chat.write': 'Chat Write',
  'chat.ask': 'Chat Ask',
  'chat.edit': 'Chat Edit',
  'summary.auto.doc': 'Auto Summary (Document)',
  'summary.auto.chunk': 'Auto Summary (Chunk)'
};

const getTaskResponseModelId = (taskKey = '') => {
  const normalizedTaskKey = normalizeTaskKey(taskKey);
  const modelKey = String(TASK_RESPONSE_MODEL_KEYS?.[normalizedTaskKey] || '').trim();
  return resolveModelIdByKey(modelKey) || null;
};

const createTaskConfig = (taskKey) => {
  return {
    key: taskKey,
    label: TASK_LABELS[taskKey] || taskKey,
    phases: {
      cheap: DEFAULT_CHEAP_MODEL_ID,
      response: getTaskResponseModelId(taskKey) || DEFAULT_RESPONSE_MODEL_ID
    }
  };
};

const TASKS = {
  'chat.explore': createTaskConfig('chat.explore'),
  'chat.write': createTaskConfig('chat.write'),
  'chat.ask': createTaskConfig('chat.ask'),
  'chat.edit': createTaskConfig('chat.edit'),
  'summary.auto.doc': createTaskConfig('summary.auto.doc'),
  'summary.auto.chunk': createTaskConfig('summary.auto.chunk')
};

const normalizePositiveNumber = (value, fallback) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return fallback;
  return numeric;
};

const normalizeActivityLogOnCompleteMode = (value, fallback = 'open') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'hidden' || normalized === 'collapsed' || normalized === 'open') {
    return normalized;
  }
  return fallback;
};

export const getAiTaskKeyForChatMode = (mode = 'Explore') => {
  const key = String(mode || '').trim().toLowerCase();
  const mapping = ROUTING?.chatModeToTask || {};
  return mapping[key] || ROUTING?.defaultChatTask || 'chat.explore';
};

export const getAiModelById = (modelId) => {
  if (!modelId) return null;
  const raw = String(modelId || '').trim();
  if (!raw) return null;
  const exact = MODEL_BY_ID.get(raw);
  if (exact) return exact;

  const candidates = getModelIdCandidates(raw);
  for (const candidate of candidates) {
    const exactCandidate = MODEL_BY_ID.get(candidate);
    if (exactCandidate) return exactCandidate;
    const normalizedCandidate = normalizeModelIdVariant(candidate);
    const resolved = MODEL_BY_NORMALIZED_ID.get(normalizedCandidate);
    if (resolved) return resolved;
  }

  return null;
};

export const getSelectableModelsForTask = () => {
  return MODELS.filter((model) => Boolean(model.selectable));
};

export const getDefaultModelForTask = (taskKey, phase = 'response') => {
  const normalizedPhase = String(phase || 'response').trim().toLowerCase();
  if (normalizedPhase !== 'response') {
    return DEFAULT_CHEAP_MODEL_ID || DEFAULT_RESPONSE_MODEL_ID;
  }

  const responseModelId = getTaskResponseModelId(taskKey) || DEFAULT_RESPONSE_MODEL_ID;
  if (responseModelId && MODEL_BY_ID.has(String(responseModelId))) return String(responseModelId);

  return DEFAULT_CHEAP_MODEL_ID;
};

export const getResolvedModelForTask = ({ taskKey, phase = 'response', requestedModelId } = {}) => {
  const requested = String(requestedModelId || '').trim();
  if (requested && requested.toLowerCase() !== 'default') {
    const model = getAiModelById(requested);
    if (model?.id) return model.id;
  }

  return getDefaultModelForTask(taskKey, phase);
};

export const getFallbackModelsForTask = ({ taskKey, primaryModelId, phase = 'response' } = {}) => {
  const primary = String(primaryModelId || getDefaultModelForTask(taskKey, phase) || '').trim();
  const tierOrder = Array.isArray(ROUTING?.fallbackTierOrder)
    ? ROUTING.fallbackTierOrder
    : ['budget', 'free', 'mid-tier', 'premium'];

  const uniqueTierOrder = Array.from(new Set([
    ...tierOrder.map((tier) => String(tier || '').trim().toLowerCase()),
    ...MODELS.map((model) => model.tier)
  ].filter(Boolean)));

  const fallbacks = [];
  for (const tier of uniqueTierOrder) {
    const tierModels = MODELS.filter((model) => model.tier === tier && model.selectable);
    for (const model of tierModels) {
      if (String(model.id) === primary) continue;
      fallbacks.push(model.id);
    }
  }

  return Array.from(new Set(fallbacks));
};

export const getChatComposerModelOptions = ({ mode = 'Explore' } = {}) => {
  const taskKey = getAiTaskKeyForChatMode(mode);
  const defaultModelId = getDefaultModelForTask(taskKey, 'response');
  const defaultModel = getAiModelById(defaultModelId);

  const selectable = getSelectableModelsForTask(taskKey).map((model) => ({
    id: model.id,
    label: model.label,
    description: model.description,
    tier: model.tier,
    isDefault: model.id === defaultModelId
  }));

  return [
    {
      id: 'default',
      label: defaultModel ? `Default (${defaultModel.label})` : 'Default',
      displayLabel: 'Default',
      description: defaultModel?.description || '',
      tier: defaultModel?.tier || 'budget',
      isDefault: true,
      resolvesTo: defaultModelId
    },
    ...selectable
  ];
};

export const getChatOrchestrationConfig = (overrides = null) => {
  const custom = overrides && typeof overrides === 'object' ? overrides : {};
  const retrieval = custom.retrieval && typeof custom.retrieval === 'object' ? custom.retrieval : {};
  const retrievalProjectSearch = retrieval.projectSearch && typeof retrieval.projectSearch === 'object'
    ? retrieval.projectSearch
    : {};
  const retrievalHotSignals = retrieval.hotSignals && typeof retrieval.hotSignals === 'object'
    ? retrieval.hotSignals
    : {};
  const retrievalAdjacentExpansion = retrievalProjectSearch.adjacentChunkExpansion
    && typeof retrievalProjectSearch.adjacentChunkExpansion === 'object'
    ? retrievalProjectSearch.adjacentChunkExpansion
    : {};
  const retrievalFullDocEscalation = retrievalProjectSearch.fullDocEscalation
    && typeof retrievalProjectSearch.fullDocEscalation === 'object'
    ? retrievalProjectSearch.fullDocEscalation
    : {};
  const retrievalChatNoise = retrievalProjectSearch.chatNoise
    && typeof retrievalProjectSearch.chatNoise === 'object'
    ? retrievalProjectSearch.chatNoise
    : {};
  const retrievalSummaries = retrieval.summaries && typeof retrieval.summaries === 'object'
    ? retrieval.summaries
    : {};
  const baseRetrievalProjectSearch = ORCHESTRATION.retrieval?.projectSearch
    && typeof ORCHESTRATION.retrieval.projectSearch === 'object'
    ? ORCHESTRATION.retrieval.projectSearch
    : {};
  const baseRetrievalHotSignals = ORCHESTRATION.retrieval?.hotSignals
    && typeof ORCHESTRATION.retrieval.hotSignals === 'object'
    ? ORCHESTRATION.retrieval.hotSignals
    : {};
  const baseRetrievalAdjacentExpansion = baseRetrievalProjectSearch.adjacentChunkExpansion
    && typeof baseRetrievalProjectSearch.adjacentChunkExpansion === 'object'
    ? baseRetrievalProjectSearch.adjacentChunkExpansion
    : {};
  const baseRetrievalFullDocEscalation = baseRetrievalProjectSearch.fullDocEscalation
    && typeof baseRetrievalProjectSearch.fullDocEscalation === 'object'
    ? baseRetrievalProjectSearch.fullDocEscalation
    : {};
  const baseRetrievalChatNoise = baseRetrievalProjectSearch.chatNoise
    && typeof baseRetrievalProjectSearch.chatNoise === 'object'
    ? baseRetrievalProjectSearch.chatNoise
    : {};
  const baseRetrievalSummaries = ORCHESTRATION.retrieval?.summaries
    && typeof ORCHESTRATION.retrieval.summaries === 'object'
    ? ORCHESTRATION.retrieval.summaries
    : {};
  const chatHistory = retrieval.chatHistory && typeof retrieval.chatHistory === 'object' ? retrieval.chatHistory : {};
  const limits = retrieval.limits && typeof retrieval.limits === 'object' ? retrieval.limits : {};
  const rollout = custom.rollout && typeof custom.rollout === 'object' ? custom.rollout : {};
  const ui = custom.ui && typeof custom.ui === 'object' ? custom.ui : {};
  const formatting = custom.formatting && typeof custom.formatting === 'object' ? custom.formatting : {};
  const queryExpansion = custom.queryExpansion && typeof custom.queryExpansion === 'object'
    ? custom.queryExpansion
    : {};
  const sufficiency = custom.sufficiency && typeof custom.sufficiency === 'object' ? custom.sufficiency : {};
  const baseSufficiency = ORCHESTRATION.sufficiency && typeof ORCHESTRATION.sufficiency === 'object'
    ? ORCHESTRATION.sufficiency
    : {};
  const refine = custom.refine && typeof custom.refine === 'object' ? custom.refine : {};
  const baseRefine = ORCHESTRATION.refine && typeof ORCHESTRATION.refine === 'object'
    ? ORCHESTRATION.refine
    : {};
  const intentContract = custom.intentContract && typeof custom.intentContract === 'object'
    ? custom.intentContract
    : {};
  const baseIntentContract = ORCHESTRATION.intentContract && typeof ORCHESTRATION.intentContract === 'object'
    ? ORCHESTRATION.intentContract
    : {};
  const router = custom.router && typeof custom.router === 'object' ? custom.router : {};
  const baseRouter = ORCHESTRATION.router && typeof ORCHESTRATION.router === 'object'
    ? ORCHESTRATION.router
    : {};
  const reranker = custom.reranker && typeof custom.reranker === 'object' ? custom.reranker : {};
  const baseReranker = ORCHESTRATION.reranker && typeof ORCHESTRATION.reranker === 'object'
    ? ORCHESTRATION.reranker
    : {};
  const consolidation = custom.consolidation && typeof custom.consolidation === 'object' ? custom.consolidation : {};
  const baseConsolidation = ORCHESTRATION.consolidation && typeof ORCHESTRATION.consolidation === 'object'
    ? ORCHESTRATION.consolidation
    : {};
  const logging = custom.logging && typeof custom.logging === 'object' ? custom.logging : {};
  const baseLogging = ORCHESTRATION.logging && typeof ORCHESTRATION.logging === 'object'
    ? ORCHESTRATION.logging
    : {};
  const sampleRate = Number.isFinite(Number(logging.payloadSampleRate))
    ? Number(logging.payloadSampleRate)
    : Number(baseLogging.payloadSampleRate || 0);
  const normalizedSampleRate = Math.max(0, Math.min(1, sampleRate));

  return {
    ...ORCHESTRATION,
    ...custom,
    enabled: custom.enabled === undefined ? ORCHESTRATION.enabled : Boolean(custom.enabled),
    rollout: {
      ...ORCHESTRATION.rollout,
      ...rollout,
      allowLegacyFallback: rollout.allowLegacyFallback === undefined
        ? ORCHESTRATION.rollout?.allowLegacyFallback
        : Boolean(rollout.allowLegacyFallback)
    },
    ui: {
      ...ORCHESTRATION.ui,
      ...ui,
      persistStageStatus: ui.persistStageStatus === undefined
        ? Boolean(ORCHESTRATION.ui?.persistStageStatus)
        : Boolean(ui.persistStageStatus),
      activityLogOnComplete: normalizeActivityLogOnCompleteMode(
        ui.activityLogOnComplete,
        normalizeActivityLogOnCompleteMode(ORCHESTRATION.ui?.activityLogOnComplete, 'open')
      )
    },
    blockedModels: Array.from(new Set(
      (Array.isArray(custom.blockedModels) ? custom.blockedModels : ORCHESTRATION.blockedModels)
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean)
    )),
    logging: {
      ...baseLogging,
      ...logging,
      enabled: logging.enabled === undefined ? Boolean(baseLogging.enabled) : Boolean(logging.enabled),
      attemptsEnabled: logging.attemptsEnabled === undefined
        ? (baseLogging.attemptsEnabled !== false)
        : Boolean(logging.attemptsEnabled),
      traceStageEventsEnabled: logging.traceStageEventsEnabled === undefined
        ? Boolean(baseLogging.traceStageEventsEnabled)
        : Boolean(logging.traceStageEventsEnabled),
      payloadsEnabled: logging.payloadsEnabled === undefined
        ? Boolean(baseLogging.payloadsEnabled)
        : Boolean(logging.payloadsEnabled),
      payloadSampleRate: normalizedSampleRate,
      maxExcerptChars: normalizePositiveNumber(logging.maxExcerptChars, baseLogging.maxExcerptChars || 700),
      attemptsIndexEnabled: logging.attemptsIndexEnabled === undefined
        ? (baseLogging.attemptsIndexEnabled !== false)
        : Boolean(logging.attemptsIndexEnabled)
    },
    queryExpansion: {
      ...ORCHESTRATION.queryExpansion,
      ...queryExpansion,
      enabled: queryExpansion.enabled === undefined
        ? ORCHESTRATION.queryExpansion?.enabled
        : Boolean(queryExpansion.enabled),
      maxQueries: normalizePositiveNumber(queryExpansion.maxQueries, ORCHESTRATION.queryExpansion?.maxQueries),
      aliasGroups: queryExpansion.aliasGroups && typeof queryExpansion.aliasGroups === 'object'
        ? queryExpansion.aliasGroups
        : (ORCHESTRATION.queryExpansion?.aliasGroups || {})
    },
    sufficiency: {
      ...baseSufficiency,
      ...sufficiency,
      enabled: sufficiency.enabled === undefined ? (baseSufficiency.enabled !== false) : Boolean(sufficiency.enabled),
      minItems: normalizePositiveNumber(sufficiency.minItems, baseSufficiency.minItems || 3),
      minChars: normalizePositiveNumber(sufficiency.minChars, baseSufficiency.minChars || 700),
      minCoverageRatio: Number.isFinite(Number(sufficiency.minCoverageRatio))
        ? Math.max(0, Math.min(1, Number(sufficiency.minCoverageRatio)))
        : Math.max(0, Math.min(1, Number(baseSufficiency.minCoverageRatio || 0.34))),
      minMatchedKeywords: normalizePositiveNumber(sufficiency.minMatchedKeywords, baseSufficiency.minMatchedKeywords || 2),
      maxRefineLoops: normalizePositiveNumber(sufficiency.maxRefineLoops, baseSufficiency.maxRefineLoops || 1)
    },
    refine: {
      ...baseRefine,
      ...refine,
      enabled: refine.enabled === undefined ? (baseRefine.enabled !== false) : Boolean(refine.enabled),
      maxExtraQueries: normalizePositiveNumber(refine.maxExtraQueries, baseRefine.maxExtraQueries || 2),
      budgetItemBoost: normalizePositiveNumber(refine.budgetItemBoost, baseRefine.budgetItemBoost || 4),
      budgetCharBoost: normalizePositiveNumber(refine.budgetCharBoost, baseRefine.budgetCharBoost || 3000),
      budgetProjectResultsBoost: normalizePositiveNumber(
        refine.budgetProjectResultsBoost,
        baseRefine.budgetProjectResultsBoost || 8
      )
    },
    intentContract: {
      ...baseIntentContract,
      ...intentContract,
      enabled: intentContract.enabled === undefined
        ? (baseIntentContract.enabled !== false)
        : Boolean(intentContract.enabled),
      alwaysOn: intentContract.alwaysOn === undefined
        ? Boolean(baseIntentContract.alwaysOn)
        : Boolean(intentContract.alwaysOn),
      forceOnLongPrompts: intentContract.forceOnLongPrompts === undefined
        ? (baseIntentContract.forceOnLongPrompts !== false)
        : Boolean(intentContract.forceOnLongPrompts),
      longPromptMinSearchableKeywords: normalizePositiveNumber(
        intentContract.longPromptMinSearchableKeywords,
        baseIntentContract.longPromptMinSearchableKeywords || 4
      ),
      maxPrimaryKeywords: normalizePositiveNumber(
        intentContract.maxPrimaryKeywords,
        baseIntentContract.maxPrimaryKeywords || 8
      ),
      maxSecondaryKeywords: normalizePositiveNumber(
        intentContract.maxSecondaryKeywords,
        baseIntentContract.maxSecondaryKeywords || 12
      ),
      maxDocHints: normalizePositiveNumber(
        intentContract.maxDocHints,
        baseIntentContract.maxDocHints || 8
      ),
      docHintMultiplier: Number.isFinite(Number(intentContract.docHintMultiplier))
        ? Math.max(1, Number(intentContract.docHintMultiplier))
        : Math.max(1, Number(baseIntentContract.docHintMultiplier || 1.35)),
      folderHintMultiplier: Number.isFinite(Number(intentContract.folderHintMultiplier))
        ? Math.max(1, Number(intentContract.folderHintMultiplier))
        : Math.max(1, Number(baseIntentContract.folderHintMultiplier || 1.2)),
      includeProjectSummary: intentContract.includeProjectSummary === undefined
        ? (baseIntentContract.includeProjectSummary !== false)
        : Boolean(intentContract.includeProjectSummary),
      includeLinkedDocSummaries: intentContract.includeLinkedDocSummaries === undefined
        ? (baseIntentContract.includeLinkedDocSummaries !== false)
        : Boolean(intentContract.includeLinkedDocSummaries),
      maxLinkedDocSummaries: normalizePositiveNumber(
        intentContract.maxLinkedDocSummaries,
        baseIntentContract.maxLinkedDocSummaries || 6
      )
    },
    router: {
      ...baseRouter,
      ...router,
      enabled: router.enabled === undefined ? Boolean(baseRouter.enabled) : Boolean(router.enabled),
      model: String(router.model || baseRouter.model || '').trim() || null,
      timeoutMs: normalizePositiveNumber(router.timeoutMs, baseRouter.timeoutMs || 2500),
      confidenceThreshold: Number.isFinite(Number(router.confidenceThreshold))
        ? Math.max(0, Math.min(1, Number(router.confidenceThreshold)))
        : Math.max(0, Math.min(1, Number(baseRouter.confidenceThreshold || 0.72))),
      maxCheapCallsPerTurn: normalizePositiveNumber(router.maxCheapCallsPerTurn, baseRouter.maxCheapCallsPerTurn || 2),
      maxTotalCallsPerTurn: normalizePositiveNumber(router.maxTotalCallsPerTurn, baseRouter.maxTotalCallsPerTurn || 3)
    },
    reranker: {
      ...baseReranker,
      ...reranker,
      enabled: reranker.enabled === undefined ? Boolean(baseReranker.enabled) : Boolean(reranker.enabled),
      model: String(reranker.model || baseReranker.model || '').trim() || null,
      timeoutMs: normalizePositiveNumber(reranker.timeoutMs, baseReranker.timeoutMs || 2500),
      topDocShareThreshold: Number.isFinite(Number(reranker.topDocShareThreshold))
        ? Math.max(0, Math.min(1, Number(reranker.topDocShareThreshold)))
        : Math.max(0, Math.min(1, Number(baseReranker.topDocShareThreshold || 0.65)))
    },
    consolidation: {
      ...baseConsolidation,
      ...consolidation,
      enabled: consolidation.enabled === undefined ? Boolean(baseConsolidation.enabled) : Boolean(consolidation.enabled),
      minContextItems: normalizePositiveNumber(consolidation.minContextItems, baseConsolidation.minContextItems || 12),
      minContextChars: normalizePositiveNumber(consolidation.minContextChars, baseConsolidation.minContextChars || 9000),
      maxInputItems: normalizePositiveNumber(consolidation.maxInputItems, baseConsolidation.maxInputItems || 20),
      maxInputChars: normalizePositiveNumber(consolidation.maxInputChars, baseConsolidation.maxInputChars || 18000),
      outputMaxChars: normalizePositiveNumber(consolidation.outputMaxChars, baseConsolidation.outputMaxChars || 2600),
      keepTopItems: Math.max(0, Number.isFinite(Number(consolidation.keepTopItems))
        ? Number(consolidation.keepTopItems)
        : Number(baseConsolidation.keepTopItems || 2)),
      model: String(consolidation.model || baseConsolidation.model || '').trim() || null
    },
    retrieval: {
      ...ORCHESTRATION.retrieval,
      ...retrieval,
      includeActiveDoc: retrieval.includeActiveDoc === undefined
        ? ORCHESTRATION.retrieval?.includeActiveDoc
        : Boolean(retrieval.includeActiveDoc),
      includeLinkedDocs: retrieval.includeLinkedDocs === undefined
        ? ORCHESTRATION.retrieval?.includeLinkedDocs
        : Boolean(retrieval.includeLinkedDocs),
      includeProjectSearch: retrieval.includeProjectSearch === undefined
        ? ORCHESTRATION.retrieval?.includeProjectSearch
        : Boolean(retrieval.includeProjectSearch),
      singleDocActiveDocBoost: normalizePositiveNumber(
        retrieval.singleDocActiveDocBoost,
        ORCHESTRATION.retrieval?.singleDocActiveDocBoost || 1.6
      ),
      singleDocStrictActiveDoc: retrieval.singleDocStrictActiveDoc === undefined
        ? Boolean(ORCHESTRATION.retrieval?.singleDocStrictActiveDoc)
        : Boolean(retrieval.singleDocStrictActiveDoc),
      projectSearch: {
        ...baseRetrievalProjectSearch,
        ...retrievalProjectSearch,
        adjacentChunkExpansion: {
          ...baseRetrievalAdjacentExpansion,
          ...retrievalAdjacentExpansion,
          enabled: retrievalAdjacentExpansion.enabled === undefined
            ? (baseRetrievalAdjacentExpansion.enabled !== false)
            : Boolean(retrievalAdjacentExpansion.enabled),
          maxChunksPerDoc: normalizePositiveNumber(
            retrievalAdjacentExpansion.maxChunksPerDoc,
            baseRetrievalAdjacentExpansion.maxChunksPerDoc || 3
          ),
          scoreDecayPerStep: Number.isFinite(Number(retrievalAdjacentExpansion.scoreDecayPerStep))
            ? Math.max(0, Number(retrievalAdjacentExpansion.scoreDecayPerStep))
            : Math.max(0, Number(baseRetrievalAdjacentExpansion.scoreDecayPerStep || 0.08))
        },
        fullDocEscalation: {
          ...baseRetrievalFullDocEscalation,
          ...retrievalFullDocEscalation,
          enabled: retrievalFullDocEscalation.enabled === undefined
            ? (baseRetrievalFullDocEscalation.enabled !== false)
            : Boolean(retrievalFullDocEscalation.enabled),
          maxDocs: normalizePositiveNumber(retrievalFullDocEscalation.maxDocs, baseRetrievalFullDocEscalation.maxDocs || 1),
          minCoverageRatio: Number.isFinite(Number(retrievalFullDocEscalation.minCoverageRatio))
            ? Math.max(0, Math.min(1, Number(retrievalFullDocEscalation.minCoverageRatio)))
            : Math.max(0, Math.min(1, Number(baseRetrievalFullDocEscalation.minCoverageRatio || 0.45))),
          maxCharsPerDoc: normalizePositiveNumber(
            retrievalFullDocEscalation.maxCharsPerDoc,
            baseRetrievalFullDocEscalation.maxCharsPerDoc || 1800
          )
        },
        chatNoise: {
          ...baseRetrievalChatNoise,
          ...retrievalChatNoise,
          enabled: retrievalChatNoise.enabled === undefined
            ? (baseRetrievalChatNoise.enabled !== false)
            : Boolean(retrievalChatNoise.enabled),
          deprioritizeMultiplier: Number.isFinite(Number(retrievalChatNoise.deprioritizeMultiplier))
            ? Math.max(0.05, Math.min(1, Number(retrievalChatNoise.deprioritizeMultiplier)))
            : Math.max(0.05, Math.min(1, Number(baseRetrievalChatNoise.deprioritizeMultiplier || 0.45))),
          perDocMaxChunks: normalizePositiveNumber(
            retrievalChatNoise.perDocMaxChunks,
            baseRetrievalChatNoise.perDocMaxChunks || 1
          )
        }
      },
      summaries: {
        ...baseRetrievalSummaries,
        ...retrievalSummaries,
        enabled: retrievalSummaries.enabled === undefined
          ? (baseRetrievalSummaries.enabled !== false)
          : Boolean(retrievalSummaries.enabled),
        rerankEnabled: retrievalSummaries.rerankEnabled === undefined
          ? (baseRetrievalSummaries.rerankEnabled !== false)
          : Boolean(retrievalSummaries.rerankEnabled),
        refreshMinCandidateScore: Number.isFinite(Number(retrievalSummaries.refreshMinCandidateScore))
          ? Math.max(0, Number(retrievalSummaries.refreshMinCandidateScore))
          : Math.max(0, Number(baseRetrievalSummaries.refreshMinCandidateScore || 0.5)),
        refreshBackfillCapPerPrompt: normalizePositiveNumber(
          retrievalSummaries.refreshBackfillCapPerPrompt,
          baseRetrievalSummaries.refreshBackfillCapPerPrompt || 16
        ),
        minChunkChars: normalizePositiveNumber(
          retrievalSummaries.minChunkChars,
          baseRetrievalSummaries.minChunkChars || 180
        ),
        rerankBoostDelta: Number.isFinite(Number(retrievalSummaries.rerankBoostDelta))
          ? Math.max(0, Number(retrievalSummaries.rerankBoostDelta))
          : Math.max(0, Number(baseRetrievalSummaries.rerankBoostDelta || 0.18)),
        rerankDownscoreDelta: Number.isFinite(Number(retrievalSummaries.rerankDownscoreDelta))
          ? Math.max(0, Number(retrievalSummaries.rerankDownscoreDelta))
          : Math.max(0, Number(baseRetrievalSummaries.rerankDownscoreDelta || 0.12)),
        rerankBoostMinMatchRatio: Number.isFinite(Number(retrievalSummaries.rerankBoostMinMatchRatio))
          ? Math.max(0, Math.min(1, Number(retrievalSummaries.rerankBoostMinMatchRatio)))
          : Math.max(0, Math.min(1, Number(baseRetrievalSummaries.rerankBoostMinMatchRatio || 0.45))),
        rerankDownscoreMaxMatchRatio: Number.isFinite(Number(retrievalSummaries.rerankDownscoreMaxMatchRatio))
          ? Math.max(0, Math.min(1, Number(retrievalSummaries.rerankDownscoreMaxMatchRatio)))
          : Math.max(0, Math.min(1, Number(baseRetrievalSummaries.rerankDownscoreMaxMatchRatio || 0.1)))
      },
      hotSignals: {
        ...baseRetrievalHotSignals,
        ...retrievalHotSignals,
        enabled: retrievalHotSignals.enabled === undefined
          ? (baseRetrievalHotSignals.enabled !== false)
          : Boolean(retrievalHotSignals.enabled),
        docNameMultiplier: Number.isFinite(Number(retrievalHotSignals.docNameMultiplier))
          ? Math.max(1, Number(retrievalHotSignals.docNameMultiplier))
          : Math.max(1, Number(baseRetrievalHotSignals.docNameMultiplier || 3)),
        folderNameMultiplier: Number.isFinite(Number(retrievalHotSignals.folderNameMultiplier))
          ? Math.max(1, Number(retrievalHotSignals.folderNameMultiplier))
          : Math.max(1, Number(baseRetrievalHotSignals.folderNameMultiplier || 2)),
        headerMultiplier: Number.isFinite(Number(retrievalHotSignals.headerMultiplier))
          ? Math.max(1, Number(retrievalHotSignals.headerMultiplier))
          : Math.max(1, Number(baseRetrievalHotSignals.headerMultiplier || 5)),
        contentKeywordScore: Number.isFinite(Number(retrievalHotSignals.contentKeywordScore))
          ? Math.max(0, Number(retrievalHotSignals.contentKeywordScore))
          : Math.max(0, Number(baseRetrievalHotSignals.contentKeywordScore || 0.2)),
        contextDocMultiplier: Number.isFinite(Number(retrievalHotSignals.contextDocMultiplier))
          ? Math.max(1, Number(retrievalHotSignals.contextDocMultiplier))
          : Math.max(1, Number(baseRetrievalHotSignals.contextDocMultiplier || 1.5)),
        headerTrailingChunks: normalizePositiveNumber(
          retrievalHotSignals.headerTrailingChunks,
          baseRetrievalHotSignals.headerTrailingChunks || 4
        ),
        headerStopAtBoundary: retrievalHotSignals.headerStopAtBoundary === undefined
          ? (baseRetrievalHotSignals.headerStopAtBoundary !== false)
          : Boolean(retrievalHotSignals.headerStopAtBoundary)
      },
      chatHistory: {
        ...ORCHESTRATION.retrieval?.chatHistory,
        ...chatHistory,
        enabled: chatHistory.enabled === undefined
          ? ORCHESTRATION.retrieval?.chatHistory?.enabled
          : Boolean(chatHistory.enabled),
        lastQaPairs: normalizePositiveNumber(chatHistory.lastQaPairs, ORCHESTRATION.retrieval?.chatHistory?.lastQaPairs),
        recencyWeight: Number.isFinite(Number(chatHistory.recencyWeight))
          ? Number(chatHistory.recencyWeight)
          : ORCHESTRATION.retrieval?.chatHistory?.recencyWeight,
        recencyDecayTurns: normalizePositiveNumber(chatHistory.recencyDecayTurns, ORCHESTRATION.retrieval?.chatHistory?.recencyDecayTurns)
      },
      limits: {
        ...ORCHESTRATION.retrieval?.limits,
        ...limits,
        maxLinkedDocs: normalizePositiveNumber(limits.maxLinkedDocs, ORCHESTRATION.retrieval?.limits?.maxLinkedDocs),
        maxProjectResults: normalizePositiveNumber(limits.maxProjectResults, ORCHESTRATION.retrieval?.limits?.maxProjectResults),
        maxContextItems: normalizePositiveNumber(limits.maxContextItems, ORCHESTRATION.retrieval?.limits?.maxContextItems),
        maxContextChars: normalizePositiveNumber(limits.maxContextChars, ORCHESTRATION.retrieval?.limits?.maxContextChars)
      }
    },
    formatting: {
      ...ORCHESTRATION.formatting,
      ...formatting,
      enabled: formatting.enabled === undefined
        ? ORCHESTRATION.formatting?.enabled
        : Boolean(formatting.enabled),
      preserveMarkdown: formatting.preserveMarkdown === undefined
        ? ORCHESTRATION.formatting?.preserveMarkdown
        : Boolean(formatting.preserveMarkdown),
      collapseExtraWhitespace: formatting.collapseExtraWhitespace === undefined
        ? ORCHESTRATION.formatting?.collapseExtraWhitespace
        : Boolean(formatting.collapseExtraWhitespace)
    }
  };
};

export const getChatOrchestrationLegacyConfig = (overrides = null) => {
  // Compatibility mapper for older CONFIG.AI.CHAT_ORCHESTRATION consumers.
  // Preferred edit surface remains src/ai/aiConfig.js.
  const orchestration = getChatOrchestrationConfig(overrides);
  const defaultCheapModelId = getDefaultModelForTask('chat.explore', 'intent');

  return {
    ENABLED: Boolean(orchestration.enabled),
    ROUTING_MODE: String(orchestration.routingMode || 'hybrid'),
    LEGACY_FALLBACK: Boolean(orchestration.rollout?.allowLegacyFallback),
    PERSIST_STAGE_STATUS_ON_SCREEN: Boolean(orchestration.ui?.persistStageStatus),
    ACTIVITY_LOG_ON_COMPLETE: normalizeActivityLogOnCompleteMode(orchestration.ui?.activityLogOnComplete),
    BLOCKED_MODELS: Array.isArray(orchestration.blockedModels)
      ? orchestration.blockedModels
      : [],
    MAX_CONTEXT_ITEMS: Number(orchestration.retrieval?.limits?.maxContextItems || 14),
    MAX_CONTEXT_CHARS: Number(orchestration.retrieval?.limits?.maxContextChars || 12000),
    MAX_LINKED_DOCS: Number(orchestration.retrieval?.limits?.maxLinkedDocs || 6),
    MAX_PROJECT_RESULTS: Number(orchestration.retrieval?.limits?.maxProjectResults || 24),
    ATTACHMENT_SUFFICIENCY_MIN_ITEMS: Number(orchestration.retrieval?.limits?.attachmentSufficiencyMinItems || 2),
    ATTACHMENT_SUFFICIENCY_MIN_CHARS: Number(orchestration.retrieval?.limits?.attachmentSufficiencyMinChars || 1200),
    SINGLE_DOC_ACTIVE_DOC_BOOST: Number(orchestration.retrieval?.singleDocActiveDocBoost || 1.6),
    SINGLE_DOC_STRICT_ACTIVE_DOC: Boolean(orchestration.retrieval?.singleDocStrictActiveDoc),
    HOT_HIT_SIGNALS_ENABLED: orchestration.retrieval?.hotSignals?.enabled !== false,
    HOT_HIT_DOC_NAME_MULTIPLIER: Number(orchestration.retrieval?.hotSignals?.docNameMultiplier || 3),
    HOT_HIT_FOLDER_NAME_MULTIPLIER: Number(orchestration.retrieval?.hotSignals?.folderNameMultiplier || 2),
    HOT_HIT_HEADER_MULTIPLIER: Number(orchestration.retrieval?.hotSignals?.headerMultiplier || 5),
    HOT_HIT_CONTENT_KEYWORD_SCORE: Number(orchestration.retrieval?.hotSignals?.contentKeywordScore || 0.2),
    HOT_HIT_CONTEXT_DOC_MULTIPLIER: Number(orchestration.retrieval?.hotSignals?.contextDocMultiplier || 1.5),
    HOT_HIT_HEADER_TRAILING_CHUNKS: Number(orchestration.retrieval?.hotSignals?.headerTrailingChunks || 4),
    HOT_HIT_HEADER_STOP_AT_BOUNDARY: orchestration.retrieval?.hotSignals?.headerStopAtBoundary !== false,
    ADJACENT_CHUNK_EXPANSION_ENABLED: orchestration.retrieval?.projectSearch?.adjacentChunkExpansion?.enabled !== false,
    ADJACENT_CHUNK_MAX_PER_DOC: Number(orchestration.retrieval?.projectSearch?.adjacentChunkExpansion?.maxChunksPerDoc || 3),
    ADJACENT_CHUNK_SCORE_DECAY: Number(orchestration.retrieval?.projectSearch?.adjacentChunkExpansion?.scoreDecayPerStep || 0.08),
    ADJACENT_CHUNK_MIN_SOURCE_SCORE: Number(orchestration.retrieval?.projectSearch?.adjacentChunkExpansion?.minSourceScore || 1.1),
    ADJACENT_CHUNK_CONTINUE_MIN_SCORE: Number(orchestration.retrieval?.projectSearch?.adjacentChunkExpansion?.continueMinScore || 0.8),
    PROJECT_CHAT_NOISE_ENABLED: orchestration.retrieval?.projectSearch?.chatNoise?.enabled !== false,
    PROJECT_SEARCH_EXCLUDE_CHAT_DOCS_WHEN_ACTIVE_CHAT: orchestration.retrieval?.projectSearch?.excludeChatDocsWhenActiveChat !== false,
    PROJECT_CHAT_NOISE_DEPRIORITIZE_MULTIPLIER: Number(
      orchestration.retrieval?.projectSearch?.chatNoise?.deprioritizeMultiplier || 0.45
    ),
    PROJECT_CHAT_NOISE_PER_DOC_MAX_CHUNKS: Number(orchestration.retrieval?.projectSearch?.chatNoise?.perDocMaxChunks || 1),
    PROJECT_CHAT_FALLBACK_MULTIPLIER: Number(orchestration.retrieval?.projectSearch?.chatNoise?.fallbackMultiplier || 0.2),
    PROJECT_NON_CHAT_PREFERENCE_MULTIPLIER: Number(orchestration.retrieval?.projectSearch?.chatNoise?.nonChatPreferenceMultiplier || 1.9),
    PROJECT_CHAT_MAX_CHUNKS: Number(orchestration.retrieval?.projectSearch?.chatNoise?.maxChunks || 8),
    PROJECT_CHAT_NOISE_SEARCH_LIMIT_MULTIPLIER: Number(orchestration.retrieval?.projectSearch?.chatNoise?.searchLimitMultiplier || 8),
    PROJECT_CHAT_NOISE_SEARCH_LIMIT_CAP: Number(orchestration.retrieval?.projectSearch?.chatNoise?.searchLimitCap || 240),
    PROJECT_SEARCH_DEEP_LIMIT_CAP: Number(orchestration.retrieval?.projectSearch?.deepLimitCap || 1200),
    PROJECT_SEARCH_MIN_NON_CHAT_ROWS: Number(orchestration.retrieval?.projectSearch?.minNonChatRows || 6),
    PROJECT_SEARCH_DOC_DIVERSITY_ENABLED: orchestration.retrieval?.projectSearch?.docDiversityEnabled !== false,
    PROJECT_SEARCH_PER_DOC_MAX_CHUNKS: Number(orchestration.retrieval?.projectSearch?.perDocMaxChunks || 2),
    PROJECT_SEARCH_PER_DOC_SCORE_DECAY: Number(orchestration.retrieval?.projectSearch?.perDocScoreDecay || 0.14),
    PROJECT_NAME_EXACT_SCORE_FLOOR: Number(orchestration.retrieval?.projectSearch?.nameGuardrails?.exactScoreFloor || 18),
    PROJECT_NAME_EXACT_MULTIPLIER: Number(orchestration.retrieval?.projectSearch?.nameGuardrails?.exactMultiplier || 2.2),
    PROJECT_NAME_PREFIX_MULTIPLIER: Number(orchestration.retrieval?.projectSearch?.nameGuardrails?.prefixMultiplier || 1.25),
    PROJECT_KEYWORD_EXACT_UNIQUE_WEIGHT: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.exactUniqueWeight || 0.09),
    PROJECT_KEYWORD_EXACT_TOTAL_WEIGHT: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.exactTotalWeight || 0.03),
    PROJECT_KEYWORD_FUZZY_UNIQUE_WEIGHT: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.fuzzyUniqueWeight || 0.03),
    PROJECT_KEYWORD_FUZZY_TOTAL_WEIGHT: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.fuzzyTotalWeight || 0.01),
    PROJECT_KEYWORD_EXACT_TOTAL_CAP: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.exactTotalCap || 10),
    PROJECT_KEYWORD_FUZZY_TOTAL_CAP: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.fuzzyTotalCap || 8),
    PROJECT_KEYWORD_SIGNAL_MULTIPLIER_CAP: Number(orchestration.retrieval?.projectSearch?.keywordSignal?.multiplierCap || 1.55),
    PROJECT_KEYWORD_MULTI_MATCH_BOOST_PER_EXTRA: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.multiKeywordBoostPerExtra || 0.3
    ),
    PROJECT_KEYWORD_MULTI_MATCH_BOOST_CAP: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.multiKeywordBoostCap || 2.2
    ),
    PROJECT_KEYWORD_DIVERSITY_RESERVE_ENABLED:
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityReserveEnabled !== false,
    PROJECT_KEYWORD_DIVERSITY_RESERVE_PER_KEYWORD: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityReservePerKeyword || 1
    ),
    PROJECT_KEYWORD_DIVERSITY_MAX_KEYWORDS: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityMaxKeywords || 4
    ),
    PROJECT_KEYWORD_DIVERSITY_DYNAMIC_RESERVE_ENABLED:
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityDynamicReserveEnabled !== false,
    PROJECT_KEYWORD_DIVERSITY_DYNAMIC_TOP_SCORE_RATIO: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityDynamicTopScoreRatio || 0.78
    ),
    PROJECT_KEYWORD_DIVERSITY_DYNAMIC_MAX_PER_KEYWORD: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityDynamicMaxPerKeyword || 4
    ),
    PROJECT_KEYWORD_DIVERSITY_DYNAMIC_MIN_SCORE: Number(
      orchestration.retrieval?.projectSearch?.keywordSignal?.diversityDynamicMinScore || 0.8
    ),
    SUMMARY_RERANK_ENABLED: orchestration.retrieval?.summaries?.enabled !== false
      && orchestration.retrieval?.summaries?.rerankEnabled !== false,
    SUMMARY_MAX_PER_TURN: Number(orchestration.retrieval?.summaries?.maxPerTurn || 16),
    SUMMARY_REFRESH_MIN_CANDIDATE_SCORE: Number(orchestration.retrieval?.summaries?.refreshMinCandidateScore || 0.5),
    SUMMARY_REFRESH_BACKFILL_CAP_PER_PROMPT: Number(orchestration.retrieval?.summaries?.refreshBackfillCapPerPrompt || 16),
    SUMMARY_MIN_CHUNK_CHARS: Number(orchestration.retrieval?.summaries?.minChunkChars || 180),
    SUMMARY_RERANK_BOOST_DELTA: Number(orchestration.retrieval?.summaries?.rerankBoostDelta || 0.18),
    SUMMARY_RERANK_DOWNSCORE_DELTA: Number(orchestration.retrieval?.summaries?.rerankDownscoreDelta || 0.12),
    SUMMARY_RERANK_BOOST_MIN_MATCH_RATIO: Number(orchestration.retrieval?.summaries?.rerankBoostMinMatchRatio || 0.45),
    SUMMARY_RERANK_DOWNSCORE_MAX_MATCH_RATIO: Number(orchestration.retrieval?.summaries?.rerankDownscoreMaxMatchRatio || 0.1),
    FULL_DOC_ESCALATION_ENABLED: orchestration.retrieval?.projectSearch?.fullDocEscalation?.enabled !== false,
    FULL_DOC_ESCALATION_MAX_DOCS: Number(orchestration.retrieval?.projectSearch?.fullDocEscalation?.maxDocs || 1),
    FULL_DOC_ESCALATION_MIN_COVERAGE_RATIO: Number(orchestration.retrieval?.projectSearch?.fullDocEscalation?.minCoverageRatio || 0.45),
    FULL_DOC_ESCALATION_MAX_CHARS_PER_DOC: Number(orchestration.retrieval?.projectSearch?.fullDocEscalation?.maxCharsPerDoc || 1800),
    HISTORY_RECENT_QA_PAIRS: Number(orchestration.retrieval?.chatHistory?.lastQaPairs || 3),
    ACTIVE_CHAT_TAIL_MAX_CHUNKS: Number(orchestration.retrieval?.chatHistory?.activeTailMaxChunks || 3),
    RECENCY_WEIGHT: Number(orchestration.retrieval?.chatHistory?.recencyWeight || 0.35),
    RECENCY_DECAY_TURNS: Number(orchestration.retrieval?.chatHistory?.recencyDecayTurns || 8),
    QUERY_EXPANSION_ENABLED: orchestration.queryExpansion?.enabled !== false,
    QUERY_EXPANSION_MAX_QUERIES: Number(orchestration.queryExpansion?.maxQueries || 3),
    QUERY_EXPANSION_ALIAS_GROUPS: orchestration.queryExpansion?.aliasGroups || {},
    SUFFICIENCY_ENABLED: orchestration.sufficiency?.enabled !== false,
    SUFFICIENCY_MIN_ITEMS: Number(orchestration.sufficiency?.minItems || 3),
    SUFFICIENCY_MIN_CHARS: Number(orchestration.sufficiency?.minChars || 700),
    SUFFICIENCY_MIN_COVERAGE_RATIO: Number(orchestration.sufficiency?.minCoverageRatio || 0.34),
    SUFFICIENCY_MIN_MATCHED_KEYWORDS: Number(orchestration.sufficiency?.minMatchedKeywords || 2),
    SUFFICIENCY_MAX_REFINE_LOOPS: Number(orchestration.sufficiency?.maxRefineLoops || 1),
    REFINE_ENABLED: orchestration.refine?.enabled !== false,
    REFINE_MAX_EXTRA_QUERIES: Number(orchestration.refine?.maxExtraQueries || 2),
    REFINE_BUDGET_ITEM_BOOST: Number(orchestration.refine?.budgetItemBoost || 4),
    REFINE_BUDGET_CHAR_BOOST: Number(orchestration.refine?.budgetCharBoost || 3000),
    REFINE_BUDGET_PROJECT_RESULTS_BOOST: Number(orchestration.refine?.budgetProjectResultsBoost || 8),
    INTENT_CONTRACT_ENABLED: orchestration.intentContract?.enabled !== false,
    INTENT_CONTRACT_ALWAYS_ON: Boolean(orchestration.intentContract?.alwaysOn),
    INTENT_CONTRACT_FORCE_ON_LONG_PROMPTS: orchestration.intentContract?.forceOnLongPrompts !== false,
    INTENT_CONTRACT_LONG_PROMPT_MIN_SEARCHABLE_KEYWORDS: Number(
      orchestration.intentContract?.longPromptMinSearchableKeywords || 4
    ),
    INTENT_CONTRACT_MAX_PRIMARY_KEYWORDS: Number(orchestration.intentContract?.maxPrimaryKeywords || 8),
    INTENT_CONTRACT_MAX_SECONDARY_KEYWORDS: Number(orchestration.intentContract?.maxSecondaryKeywords || 12),
    INTENT_CONTRACT_MAX_DOC_HINTS: Number(orchestration.intentContract?.maxDocHints || 8),
    INTENT_CONTRACT_DOC_HINT_MULTIPLIER: Number(orchestration.intentContract?.docHintMultiplier || 1.35),
    INTENT_CONTRACT_FOLDER_HINT_MULTIPLIER: Number(orchestration.intentContract?.folderHintMultiplier || 1.2),
    INTENT_CONTRACT_INCLUDE_PROJECT_SUMMARY: orchestration.intentContract?.includeProjectSummary !== false,
    INTENT_CONTRACT_INCLUDE_LINKED_DOC_SUMMARIES: orchestration.intentContract?.includeLinkedDocSummaries !== false,
    INTENT_CONTRACT_MAX_LINKED_DOC_SUMMARIES: Number(orchestration.intentContract?.maxLinkedDocSummaries || 6),
    ROUTER_ENABLED: Boolean(orchestration.router?.enabled),
    ROUTER_MODEL: String(orchestration.router?.model || defaultCheapModelId || '').trim() || null,
    ROUTER_TIMEOUT_MS: Number(orchestration.router?.timeoutMs || 2500),
    ROUTER_CONFIDENCE_THRESHOLD: Number(orchestration.router?.confidenceThreshold || 0.72),
    MAX_CHEAP_CALLS_PER_TURN: Number(orchestration.router?.maxCheapCallsPerTurn || 2),
    MAX_TOTAL_CALLS_PER_TURN: Number(orchestration.router?.maxTotalCallsPerTurn || 3),
    RERANKER_ENABLED: Boolean(orchestration.reranker?.enabled),
    RERANKER_MODEL: String(orchestration.reranker?.model || defaultCheapModelId || '').trim() || null,
    RERANKER_TIMEOUT_MS: Number(orchestration.reranker?.timeoutMs || 2500),
    RERANKER_TOP_DOC_SHARE_THRESHOLD: Number(orchestration.reranker?.topDocShareThreshold || 0.65),
    CONSOLIDATION_ENABLED: Boolean(orchestration.consolidation?.enabled),
    CONSOLIDATION_MIN_CONTEXT_ITEMS: Number(orchestration.consolidation?.minContextItems || 12),
    CONSOLIDATION_MIN_CONTEXT_CHARS: Number(orchestration.consolidation?.minContextChars || 9000),
    CONSOLIDATION_MAX_INPUT_ITEMS: Number(orchestration.consolidation?.maxInputItems || 20),
    CONSOLIDATION_MAX_INPUT_CHARS: Number(orchestration.consolidation?.maxInputChars || 18000),
    CONSOLIDATION_OUTPUT_MAX_CHARS: Number(orchestration.consolidation?.outputMaxChars || 2600),
    CONSOLIDATION_KEEP_TOP_ITEMS: Number(orchestration.consolidation?.keepTopItems || 2),
    CONSOLIDATION_SUMMARY_MAX_PER_TURN: Number(orchestration.consolidation?.summaryHarvestMaxPerTurn || 6),
    CONSOLIDATION_SUMMARY_MIN_SCORE: Number(orchestration.consolidation?.summaryHarvestMinScore || 0.75),
    CONSOLIDATION_MODEL: String(orchestration.consolidation?.model || defaultCheapModelId || '').trim() || null,
    LOGGING_ENABLED: orchestration.logging?.enabled !== false,
    LOG_ATTEMPTS_ENABLED: orchestration.logging?.attemptsEnabled !== false,
    LOG_TRACE_STAGE_EVENTS_ENABLED: Boolean(orchestration.logging?.traceStageEventsEnabled),
    LOG_PAYLOADS_ENABLED: Boolean(orchestration.logging?.payloadsEnabled),
    LOG_PAYLOAD_SAMPLE_RATE: Number.isFinite(Number(orchestration.logging?.payloadSampleRate))
      ? Math.max(0, Math.min(1, Number(orchestration.logging.payloadSampleRate)))
      : 0,
    LOG_MAX_EXCERPT_CHARS: Number(orchestration.logging?.maxExcerptChars || 700),
    LOG_ATTEMPTS_INDEX_ENABLED: orchestration.logging?.attemptsIndexEnabled !== false
  };
};

export const isChatOrchestrationEnabled = (overrides = null) => {
  return Boolean(getChatOrchestrationConfig(overrides).enabled);
};

export const AI_RUNTIME_CONFIG = {
  models: MODELS,
  tasks: TASKS
};
